/**
 * The tab strip over the composer's workspace.
 *
 * Two tabs, one visible at a time. They swap what fills the working area:
 * the post itself, or the per-network settings for it. Media is not a tab —
 * uploading and attaching live inside Compose, next to the caption they belong
 * to, and the library rail is always on screen regardless.
 */

import { cn } from '../../../lib/utils';

export type WorkspacePanel = 'compose' | 'settings';

export const DEFAULT_WORKSPACE_PANEL: WorkspacePanel = 'compose';

interface WorkspaceTabsProps {
  tabs: { panel: WorkspacePanel; label: string }[];
  active: WorkspacePanel;
  onChange: (panel: WorkspacePanel) => void;
}

export function WorkspaceTabs({ tabs, active, onChange }: Readonly<WorkspaceTabsProps>) {
  return (
    <div role="tablist" className="flex min-w-0 flex-1">
      {tabs.map(({ panel, label }, index) => {
        const isActive = panel === active;

        return (
          <button
            key={panel}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(panel)}
            className={cn(
              'relative flex-1 truncate px-3 py-2.5 text-[13px] transition-colors',
              index > 0 && 'border-l border-slate-200',
              isActive
                ? 'bg-white font-bold text-blue-700'
                : 'bg-slate-50/80 font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            )}
          >
            {label}
            {isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
          </button>
        );
      })}
    </div>
  );
}
